import type { ICountryObject } from "app/services/CountryService";
import type { IRegionObject } from "app/services/RegionService";
import RegionService from "app/services/RegionService";

/** Synthetic combobox region for countries not listed on any real region. */
export const OTHER_COUNTRIES_REGION_NAME = "__employee_location_other_countries__";
const OTHER_COUNTRIES_REGION_ID = -900_010_002;

export type CountryRegionGroup<TCountry extends { id: number }> = {
    region: IRegionObject;
    countries: TCountry[];
};

export type NestedRegionGroup<TCountry extends { id: number }> = {
    region: IRegionObject;
    /** Direct child rows (leftover countries on parents; all members on leaf groups). */
    countries: TCountry[];
    subGroups: NestedRegionGroup<TCountry>[];
    /** Full membership for checkbox state and count when not searching. */
    memberCountries: TCountry[];
};

export type RegionGroup = NestedRegionGroup<ICountryObject>;
export type LocationGroups = { groups: RegionGroup[] };
export type RegionCheckState = "checked" | "partial" | "unchecked";

const MAJORITY_CONTAINMENT_THRESHOLD = 0.5;

const countRegionCountryIntersection = (childIds: readonly number[], parentIds: readonly number[]): number => {
    const parentSet = new Set(parentIds);
    let count = 0;
    for (const id of childIds) {
        if (parentSet.has(id)) {
            count++;
        }
    }
    return count;
};

const findNestedParentIndex = <TCountry extends { id: number }>(
    childIndex: number,
    groups: readonly CountryRegionGroup<TCountry>[],
): number | undefined => {
    const child = groups[childIndex];
    const childIds = child.region.countries;
    const childSize = childIds.length;

    if (childSize === 0 || child.region.name === OTHER_COUNTRIES_REGION_NAME) {
        return undefined;
    }

    let bestParent: number | undefined;
    let bestRatio = 0;
    let bestParentSize = Infinity;

    for (let parentIndex = 0; parentIndex < groups.length; parentIndex++) {
        if (parentIndex === childIndex) {
            continue;
        }

        const parent = groups[parentIndex];
        if (parent.region.name === OTHER_COUNTRIES_REGION_NAME) {
            continue;
        }

        const parentIds = parent.region.countries;
        const parentSize = parentIds.length;
        if (parentSize <= childSize) {
            continue;
        }

        const intersection = countRegionCountryIntersection(childIds, parentIds);
        const ratio = intersection / childSize;
        if (ratio <= MAJORITY_CONTAINMENT_THRESHOLD) {
            continue;
        }

        if (bestParent === undefined || ratio > bestRatio || (ratio === bestRatio && parentSize < bestParentSize)) {
            bestParent = parentIndex;
            bestRatio = ratio;
            bestParentSize = parentSize;
        }
    }

    return bestParent;
};

export const nestRegionGroups = <TCountry extends { id: number }>(
    flatGroups: readonly CountryRegionGroup<TCountry>[],
): NestedRegionGroup<TCountry>[] => {
    const childToParent = new Map<number, number>();

    for (let index = 0; index < flatGroups.length; index++) {
        const parentIndex = findNestedParentIndex(index, flatGroups);
        if (parentIndex !== undefined) {
            childToParent.set(index, parentIndex);
        }
    }

    const buildNested = (group: CountryRegionGroup<TCountry>, index: number): NestedRegionGroup<TCountry> => {
        const memberCountries = [...group.countries];

        const childIndices = flatGroups
            .map((_, groupIndex) => groupIndex)
            .filter((groupIndex) => childToParent.get(groupIndex) === index);

        const subGroups = childIndices.map((groupIndex) => buildNested(flatGroups[groupIndex], groupIndex));

        const idsInSubGroups = new Set<number>();
        for (const subGroup of subGroups) {
            for (const country of subGroup.memberCountries) {
                idsInSubGroups.add(country.id);
            }
        }

        const directCountries = memberCountries.filter((country) => !idsInSubGroups.has(country.id));

        return {
            region: group.region,
            countries: directCountries,
            subGroups,
            memberCountries,
        };
    };

    return flatGroups
        .map((_, index) => index)
        .filter((index) => !childToParent.has(index))
        .map((index) => buildNested(flatGroups[index], index));
};

const resolveRegionByName = (name: string, regions: readonly IRegionObject[]): IRegionObject | undefined =>
    regions.find((r) => r.name === name) ?? RegionService.getRegionByName(name);

export const buildCountryRegionGroups = <TCountry extends { id: number }>(
    regions: readonly IRegionObject[],
    countries: readonly TCountry[],
    otherCountriesLabel: string,
): CountryRegionGroup<TCountry>[] => {
    const idsInAnyRegion = new Set<number>();
    for (const region of regions) {
        for (const id of region.countries) {
            idsInAnyRegion.add(id);
        }
    }

    const groups: CountryRegionGroup<TCountry>[] = [];

    for (const region of regions) {
        const regionCountries: TCountry[] = [];
        for (const country of countries) {
            if (region.countries.includes(country.id)) {
                regionCountries.push(country);
            }
        }
        if (regionCountries.length > 0) {
            groups.push({ region, countries: regionCountries });
        }
    }

    const ungrouped = countries.filter((c) => !idsInAnyRegion.has(c.id));
    if (ungrouped.length > 0) {
        groups.push({
            region: {
                id: OTHER_COUNTRIES_REGION_ID,
                name: OTHER_COUNTRIES_REGION_NAME,
                text: otherCountriesLabel,
                countries: ungrouped.map((c) => c.id),
            },
            countries: ungrouped,
        });
    }

    return groups;
};

export const buildLocationGroups = (
    regions: readonly IRegionObject[],
    countries: readonly ICountryObject[],
    otherCountriesLabel: string,
): LocationGroups => ({
    groups: nestRegionGroups(buildCountryRegionGroups(regions, countries, otherCountriesLabel)),
});

export const getSelectedCodes = (
    value: { codes?: readonly number[]; regions?: readonly string[] } | undefined,
    regions: readonly IRegionObject[],
): Set<number> => {
    const set = new Set<number>();
    for (const code of value?.codes ?? []) {
        set.add(code);
    }
    for (const name of value?.regions ?? []) {
        const region = resolveRegionByName(name, regions);
        if (region) {
            for (const id of region.countries) {
                set.add(id);
            }
        }
    }
    return set;
};

export const getRegionState = (
    visibleChildIds: readonly number[],
    selectedCodes: ReadonlySet<number>,
): RegionCheckState => {
    if (visibleChildIds.length === 0) {
        return "unchecked";
    }
    let selected = 0;
    for (const id of visibleChildIds) {
        if (selectedCodes.has(id)) {
            selected++;
        }
    }
    if (selected === 0) {
        return "unchecked";
    }
    if (selected === visibleChildIds.length) {
        return "checked";
    }
    return "partial";
};

export const toggleRegion = (visibleChildIds: readonly number[], selectedCodes: ReadonlySet<number>): number[] => {
    if (visibleChildIds.length === 0) {
        return [...selectedCodes];
    }
    const visibleSet = new Set(visibleChildIds);
    const allSelected = visibleChildIds.every((id) => selectedCodes.has(id));
    if (allSelected) {
        return [...selectedCodes].filter((id) => !visibleSet.has(id));
    }
    const out = [...selectedCodes];
    for (const id of visibleChildIds) {
        if (!selectedCodes.has(id)) {
            out.push(id);
        }
    }
    return out;
};

export const toggleCountry = (countryId: number, selectedCodes: ReadonlySet<number>): number[] => {
    if (selectedCodes.has(countryId)) {
        return [...selectedCodes].filter((id) => id !== countryId);
    }
    return [...selectedCodes, countryId];
};

export type CollapsedRegionSelection = {
    region: IRegionObject;
    memberCountryIds: readonly number[];
};

export type CollapsedLocationSelection = {
    regions: readonly CollapsedRegionSelection[];
    countryIds: readonly number[];
};

const collapseGroupIntoSelection = (
    group: RegionGroup,
    selectedCodes: ReadonlySet<number>,
    coveredIds: Set<number>,
    regions: CollapsedRegionSelection[],
): void => {
    if (group.region.name === OTHER_COUNTRIES_REGION_NAME) {
        return;
    }

    const memberIds = group.memberCountries.map((country) => country.id);
    const allSelected = memberIds.length > 0 && memberIds.every((id) => selectedCodes.has(id));

    if (allSelected) {
        regions.push({
            region: group.region,
            memberCountryIds: memberIds,
        });
        for (const id of memberIds) {
            coveredIds.add(id);
        }
        return;
    }

    for (const subGroup of group.subGroups) {
        collapseGroupIntoSelection(subGroup, selectedCodes, coveredIds, regions);
    }
};

/** Collapse fully selected regions into region chips; leftover selected ids become country chips. */
export const collapseSelectedLocationCodes = (
    selectedCodes: ReadonlySet<number>,
    groups: readonly RegionGroup[],
): CollapsedLocationSelection => {
    const regions: CollapsedRegionSelection[] = [];
    const coveredIds = new Set<number>();

    for (const group of groups) {
        collapseGroupIntoSelection(group, selectedCodes, coveredIds, regions);
    }

    const countryIds = [...selectedCodes].filter((id) => !coveredIds.has(id)).sort((a, b) => a - b);

    return { regions, countryIds };
};
